import React, { useState, useEffect, useMemo } from "react";
import DeviceApi from "../../services/DeviceApi";
import { Device } from "../../components/Filters/types/types";
import { useTranslation } from 'react-i18next';

import IApiResponseGetDevicesParam from "../../interfaces/IApiResponseGetDevicesParam";
import IDropDownOption from "../../interfaces/IDropDowmOption";

// Design System
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import Modal from "../../components/Modal/Modal";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

interface DeviceParamDialogProps {
    visible: boolean;
    onHide: () => void;
    onCreate: (data: IApiResponseGetDevicesParam) => Promise<void>;
    device: { id: string, deviceId: string, description?: string; paramKey?: string; paramValue?: string, active: boolean } | null;
}

const DeviceParamDialog: React.FC<DeviceParamDialogProps> = ({ visible, onHide, onCreate, device }) => {
    const [description, setDescription] = useState("");
    const [paramKey, setParamKey] = useState("");
    const [paramValue, setParamValue] = useState("");
    const [devices, setDevices] = useState<Device[]>([]);
    const [selectedDeviceParam, setSelectedDeviceParam] = useState<Device | null>(null);
    const [selectedDropdownValue, setSelectedDropdownValue] = useState<IDropDownOption | null>(null);
    const [errors, setErrors] = useState<{ name?: string; description?: string; paramKey?: string; paramValue?: string; status?: string;}>({});
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);

    const { t: translateMessage } = useTranslation("messages");
    const { t: translateForm } = useTranslation("form");
    const { t: translateGeneral } = useTranslation("general");

    const dropdownOptions: IDropDownOption[] = useMemo(() => [
        { id: 1, description: translateGeneral("ok"), boolean: true },
        { id: 2, description: translateGeneral("notOk"), boolean: false }
    ], [translateGeneral]);

    const isDirty = useMemo(() => {
        if (device) {
            return paramKey !== device.paramKey || 
                   paramValue !== device.paramValue || 
                   description !== device.description || 
                   selectedDeviceParam?.id !== device.deviceId ||
                   selectedDropdownValue?.boolean !== device.active;
        }
        return paramKey !== "" || paramValue !== "" || description !== "" || selectedDeviceParam !== null || selectedDropdownValue !== null;
    }, [paramKey, paramValue, description, selectedDeviceParam, selectedDropdownValue, device]);

    useEffect(() => {
        if (visible) {
            fetchAllDevices();
            if (device) {
                setParamKey(device.paramKey || "");
                setParamValue(device.paramValue || "");
                setDescription(device.description || "");
                const option = dropdownOptions.find(opt => opt.boolean === device.active);
                setSelectedDropdownValue(option || null);
            } else {
                setParamKey("");
                setParamValue("");
                setDescription("");
                setSelectedDeviceParam(null);
                setSelectedDropdownValue(null);
            }
            setErrors({});
        }
    }, [visible, device, dropdownOptions]);

    const handleCreate = async (): Promise<void> => {
        const newErrors: typeof errors = {};

        if (!selectedDeviceParam) {
            newErrors.name = translateMessage("selectDevice");
        }

        if (!paramKey) {
            newErrors.paramKey = translateMessage("keyRequired");
        }
        if (!paramValue) {
            newErrors.paramValue = translateMessage("valueRequired");
        }
        if (!description) {
            newErrors.description = translateMessage("descriptionRequired");
        }

        if (!selectedDropdownValue) {
            newErrors.status = translateMessage("statusRequired");
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        const idToSubmit = device ? device.id : "";

        await onCreate({
            id: idToSubmit,
            paramKey,
            paramValue,
            description,
            active: selectedDropdownValue ? selectedDropdownValue.boolean : false,
            deviceId: selectedDeviceParam!.id,
            createDate: ""
        });

        onHide();
    };

    const fetchAllDevices = async (): Promise<void> => {
        try {
            const response = await DeviceApi.GetAllDevices();
            if (response.data && Array.isArray(response.data)) {
                const devicesData: Device[] = response.data.map((device: any) => ({
                    id: device.id,
                    name: device.name,
                    description: device.description,
                }));
                setDevices(devicesData);
                
                if (device) {
                    const selected = devicesData.find(dev => dev.id === device.deviceId);
                    setSelectedDeviceParam(selected || null);
                }
            }
        } catch (error) {
            console.error(translateMessage("error"), error);
        }
    };

    const handleDeviceChange = (val: any): void => {
        const dev = devices.find(d => d.id === val);
        setSelectedDeviceParam(dev || null);
        if (val) {
            setErrors((prev) => ({ ...prev, name: undefined }));
        }
    };

    const handleActiveChange = (val: any): void => {
        const option = dropdownOptions.find(opt => opt.id === Number(val));
        setSelectedDropdownValue(option || null);
        if (val) {
            setErrors((prev) => ({ ...prev, status: undefined }));
        }
    };

    const handleCancel = (): void => {
        if (isDirty) {
            setShowConfirmCancel(true);
        } else {
            onHide();
        }
    };

    return (
        <>
            <Modal
                header={device ? translateGeneral("editDeviceParam") : translateGeneral("newDeviceParam")}
                visible={visible}
                onHide={handleCancel}
                size="md"
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                        <Button
                            onClick={handleCancel}
                            label={translateGeneral("cancel")}
                            severity="secondary"
                            variant="text"
                        />
                        <Button
                            onClick={handleCreate}
                            label={device ? translateGeneral("update") : translateGeneral("register")}
                            icon="save"
                            severity="primary"
                        />
                    </div>
                }
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem", paddingTop: "0.5rem" }}>
                    <Input
                        type="select"
                        label={translateForm("device")}
                        value={selectedDeviceParam?.id || ""}
                        options={devices.map(d => ({ label: d.name, value: d.id, id: d.id }))}
                        onChange={handleDeviceChange}
                        required
                    />
                    {errors.name && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.name}</small>}
                    
                    <Input
                        type="select"
                        label={translateForm("active")}
                        value={selectedDropdownValue?.id.toString() || ""}
                        options={dropdownOptions.map(opt => ({ label: opt.description, value: opt.id.toString(), id: opt.id.toString() }))}
                        onChange={handleActiveChange}
                        required
                    />
                    {errors.status && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.status}</small>}

                    <Input
                        type="text"
                        label={translateForm("paramKey")}
                        value={paramKey}
                        onChange={(val: any) => {
                            setParamKey(val);
                            setErrors((prev) => ({ ...prev, paramKey: undefined }));
                        }}
                        required
                    />
                    {errors.paramKey && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.paramKey}</small>}

                    <Input
                        type="text"
                        label={translateForm("paramValue")}
                        value={paramValue}
                        onChange={(val: any) => {
                            setParamValue(val);
                            setErrors((prev) => ({ ...prev, paramValue: undefined }));
                        }}
                        required
                    />
                    {errors.paramValue && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.paramValue}</small>}

                    <Input
                        type="text"
                        label={translateForm("description")}
                        value={description}
                        onChange={(val: any) => {
                            setDescription(val);
                            setErrors((prev) => ({ ...prev, description: undefined }));
                        }}
                        required
                    />
                    {errors.description && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.description}</small>}
                </div>
            </Modal>

            <ConfirmModal
                visible={showConfirmCancel}
                onHide={() => setShowConfirmCancel(false)}
                onConfirm={() => {
                    setShowConfirmCancel(false);
                    onHide();
                }}
            />
        </>
    );
};

export default DeviceParamDialog;
